import { computed, type Ref, ref, toRefs } from 'vue';

import { useProductFamilyStore } from '@/store/pinia/product-family';

import { useFischer } from './fischerPlugin';

/**
 * Composable for managing auto-filters functionality
 * This provides automatic filtering capabilities for product lists
 */
export const useAutoFiltersComposable = (filtersParam: Ref<Array<any>>) => {
  const fischer = useFischer();
  const { activeFacets } = toRefs(useProductFamilyStore());

  const isAutofiltersSite = !fischer.app.isFite() && !fischer.app.isUpat();
  const useAutoFilters = ref<boolean>(isAutofiltersSite);

  const enableAutoFilters = () => {
    if (isAutofiltersSite) {
      useAutoFilters.value = true;
    }
  };

  const disableAutoFilters = () => {
    useAutoFilters.value = false;
  };

  const autoFilters = computed(() => {
    if (filtersParam.value.length === 0 && useAutoFilters.value) {
      const isDiy = fischer.user.isDiy();
      const isProfi = fischer.user.isProfi();
      if (isDiy && !isProfi) {
        return [
          {
            associatedFieldName: 'Profi / DIY_A',
            name: 'DIY~~~!Profi',
            displayField: 'Profi / DIY (DIY)',
            fakeValue: 'DIY',
          },
        ];
      }
      if (!isDiy && isProfi) {
        return [
          {
            associatedFieldName: 'Profi / DIY_A',
            name: 'Profi~~~!DIY',
            displayField: 'Profi / DIY (Profi)',
            fakeValue: 'Profi',
          },
        ];
      }
    }

    return null;
  });

  const applyChanges = (changes: any[], multiSelect?: boolean) => {
    let filters = useAutoFilters.value
      ? (autoFilters.value || []).map((filter) => ({
          ...filter,
          name: filter.fakeValue || filter.name,
        }))
      : filtersParam.value;
    changes.forEach(({ element, value }) => {
      // remove old filter if already active
      const oldFiltersLength = filters.length;
      if (multiSelect) {
        filters = filters.filter(
          (oldFacet: any) =>
            !(
              oldFacet.associatedFieldName === element.associatedFieldName &&
              oldFacet.name === element.name
            ),
        );
      } else {
        filters = filters.filter(
          (oldFacet: any) => oldFacet.associatedFieldName !== element.associatedFieldName,
        );
      }

      if (oldFiltersLength !== filters.length) {
        disableAutoFilters();
      }

      // add to active filters
      if (value) {
        filters.push(element);
        disableAutoFilters();
      }
    });

    if (!useAutoFilters.value) {
      // sort for better cache hits and consistent urls
      // there should not be too many filters, so performance overhead for localeCompare should not matter
      filters.sort((first: any, second: any) =>
        first.associatedFieldName.localeCompare(second.associatedFieldName),
      );

      filtersParam.value = filters;
      activeFacets.value = filters as any;
    }
  };

  return {
    applyChanges,
    autoFilters,
    disableAutoFilters,
    enableAutoFilters,
    useAutoFilters,
  };
};
